import { BaseToggleButtons } from "@ui/atoms/BaseToggleButtons/BaseToggleButtons";
import { HeaderTabConfig } from "@ui/organisms/Header/HeaderTabs";

export interface HeaderToggleTabsProps {
  loading?: boolean;
  tabIndex: number;
  onChange: (newIndex: number) => Promise<void> | void;
  tabs: HeaderTabConfig[];
}

/**
 * Primary page-navigation tabs rendered as pill-style `BaseToggleButtons`,
 * a drop-in replacement for the underline `HeaderTabs` on top-level pages.
 * Rendered as a sibling directly below `<Header>` (same pattern as
 * `DashboardShell`). Keeps the index-based `HeaderTabConfig` shape — including
 * `count` badges — and adapts it to the value-based toggle API.
 */
export const HeaderToggleTabs = ({
  loading = false,
  onChange,
  tabIndex,
  tabs
}: HeaderToggleTabsProps) => {
  if (!tabs.length) {
    return null;
  }

  return (
    // No scroll wrapper: BaseToggleButtons owns its own overflow handling
    // (hidden scrollbar + arrow buttons). An outer `overflow: auto` here would
    // scroll instead, so the group never overflows and the arrows never appear.
    <BaseToggleButtons
      loading={loading}
      value={tabIndex}
      handleChange={(_event, newIndex) => {
        if (newIndex != null) {
          onChange(newIndex);
        }
      }}
      buttons={tabs.map((tab, index) => ({
        count: tab.count,
        dataTestId: tab.dataTestId,
        disabled: tab.disabled,
        label: tab.label,
        value: tab.index ?? index
      }))}
    />
  );
};
